import React from 'react'
import { Input, Select, SelectContent, SelectTrigger, SelectItem, SelectValue, Icon, IconButton } from '@shopflo/ui'
import { CurrencyInr, Percent } from '@phosphor-icons/react'
import {
  DiscountApplyToType,
  BundleEntitlement,
  UiConfigDiscountType,
  CouponType,
  EntitledCatalogueSelectionMode
} from 'src/lib/types/discountsV2'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import FormErrorText from 'src/components/common/FormErrorText'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'

const BundlePricingInputs = () => {
  const {
    state: { entitlement: rawEntitlement, uiConfig: rawUiConfig, errors },
    actions: { setFormState }
  } = useDiscountForm()
  const { t } = useLocale()

  if (rawEntitlement.type !== CouponType.BUNDLE || rawUiConfig.type !== UiConfigDiscountType.BUNDLE) return null

  const entitlement = rawEntitlement
  const { concessionAmount, entitledProductCount, entitledCatalogue } = entitlement

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      entitlement: {
        ...entitlement,
        concessionAmount: parseInt(e.target.value)
      }
    })
  }

  const handleEntitledProductCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = parseInt(e.target.value) || 0

    if (val > 999999) {
      val = 999999
    }
    const newCatalogue = [...entitledCatalogue]
    if (newCatalogue.length > 0) {
      newCatalogue[0] = { ...newCatalogue[0], quantity: val }
    }

    setFormState({
      entitlement: {
        ...entitlement,
        entitledProductCount: val,
        entitledCatalogue: newCatalogue
      }
    })
  }

  const handleApplyToChange = (value: string) => {
    setFormState({
      entitlement: {
        ...entitlement,
        entitledCatalogue: [
          {
            quantity: 1,
            entities: [],
            selectionMode: EntitledCatalogueSelectionMode.ANY
          }
        ],
        excludedCatalogue: [],
        metaData: {
          ...entitlement.metaData,
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        applyTo: value as DiscountApplyToType
      }
    })
  }

  const handleBlur = () => {
    if (!concessionAmount || concessionAmount === 0) {
      setFormState({
        entitlement: {
          ...entitlement,
          concessionAmount: 1
        }
      })
    }
  }

  const showGlobalQuantity = (entitledCatalogue?.length || 0) <= 1

  return (
    <div className='flex flex-col gap-1 w-full'>
      <div className='flex gap-3 w-full'>
        <div className='flex flex-1 gap'>
          <Input
            type='number'
            value={concessionAmount}
            onChange={handleAmountChange}
            onBlur={handleBlur}
            dimension='md'
            startAdornment={
              <div className='flex items-center h-full gap-1 px-0 pl-1'>
                <Icon IconComponent={CurrencyInr} size='sm' className='text-zinc-500' />
              </div>
            }
            className={classNames(
              'text-zinc-800 !justify-start relative z-[1] focus-within:z-[2]',
              showGlobalQuantity ? 'basis-[70%] rounded-r-none border-r-0' : 'flex-1'
            )}
          />
          {showGlobalQuantity && (
            <Input
              type='number'
              value={entitledProductCount || ''}
              onChange={handleEntitledProductCountChange}
              dimension='md'
              style={{ width: `${(entitledProductCount?.toString().length || 0) + 2}ch` }}
              startAdornment={
                <div className='flex items-center h-full'>
                  <span className='text-text-sm-regular text-zinc-400 whitespace-nowrap'>
                    {t('discounts_v2.for_any')}
                  </span>
                </div>
              }
              className='basis-[30%] rounded-l-none relative z-[1] focus-within:z-[2]'
            />
          )}
        </div>
        <div className='flex-1'>
          <Select value={entitlement.applyTo} onValueChange={handleApplyToChange}>
            <SelectTrigger className='w-full h-9 bg-white rounded-lg'>
              <SelectValue placeholder={t('discounts_v2.select_products')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DiscountApplyToType.ALL_PRODUCTS}>{t('discounts_v2.from_all_products')}</SelectItem>
              <SelectItem value={DiscountApplyToType.SPECIFIC_PRODUCTS}>
                {t('discounts_v2.from_specific_products')}
              </SelectItem>
              <SelectItem value={DiscountApplyToType.SPECIFIC_COLLECTIONS}>
                {t('discounts_v2.from_specific_collections')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <FormErrorText message={errors['entitlement.concessionAmount']} />
      {showGlobalQuantity && <FormErrorText message={errors['entitlement.entitledProductCount']} />}
    </div>
  )
}

export default BundlePricingInputs
